import React, { useEffect, useRef, useState } from "react";
import { useHistory, withRouter } from "react-router-dom";
import { compose } from "redux";
import { useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { message } from "antd";
import clsx from "clsx";
import AuthenticationApiSerivce from "../../../../api/AuthServices";
import {
  isRegistrationLoadRetryExhaustedError,
  isRequestCancelledError,
} from "../../../../api/requestRetry";
import { resolveUserSubmitErrorMessage } from "../../../../api/submitRequestErrors";
import { useLockViewportScroll } from "../../../../hook/useLockViewportScroll";
import {
  REGISTRATION_STEP_TITLE_PROPS,
  useRegistrationStepFocus,
} from "../../../../hook/useRegistrationStepFocus";
import { hasRegistrationSignupSession } from "../../registrationAuth";
import {
  extractCardLastFour,
  getRegistrationData,
  isCardOnFileComplete,
  isRegistrationComplete,
  updateRegistrationData,
} from "../../registrationSetupStorage";
import { getStoredPaymentTypeFields } from "../../registrationPaymentType";
import {
  redirectIfProviderUnavailableBeforeApi,
  redirectIfProviderUnavailableFromResponse,
} from "../../registrationRdAvailability";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import {
  focusFirstInvalidRegistrationField,
  getRegistrationFieldControlProps,
  getRegistrationFieldErrorId,
  RegistrationContinueArrow,
  RegistrationFieldError,
} from "../../../RegistrationLayout/RegistrationFieldError";
import { RegistrationRequiredMark, RegistrationAnnouncedText } from "../../../RegistrationLayout/RegistrationA11y";
import {
  REGISTRATION_ERROR,
  REGISTRATION_PLACEHOLDER,
} from "../../../RegistrationLayout/registrationTheme";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import CancellationPolicyModal from "../CancellationPolicyModal/CancellationPolicyModal";
import CardDetailsConfirmModal from "../CardDetailsConfirmModal/CardDetailsConfirmModal";
import enhancer, {
  cardOnFileValidationMessageFallbacks,
  extractPaymentMethodIdFromSetupIntent,
  extractPublishableKey,
  extractRegistrationCardsFromListResponse,
  extractSetupIntentClientSecret,
  extractSetupIntentId,
  getStoredSetupIntentId,
  isStripeApiSuccess,
  normalizeRegistrationCardFromApi,
  pickDefaultRegistrationCard,
} from "./RegistrationCardOnFile.validator";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import { registrationStepBack } from "../../registrationNavigation";
import {
  trackRegistrationError,
  trackRegistrationStepCompleted,
} from "../../../../helper/registrationAnalytics";
import { getRegistrationServiceId } from "../../Appointment/calendlyBooking";

const CARD_ON_FILE_STEP = REGISTRATION_SETUP_STEP_META.CARD_ON_FILE;
const CARD_ON_FILE_INSURANCE_STEP =
  REGISTRATION_SETUP_STEP_META.CARD_ON_FILE_INSURANCE;

const CARD_ON_FILE_FIELD_FOCUS_ORDER = [
  { field: "cardholderName", id: "cardholderName" },
  { field: "cardNumberElement", selector: "#cardNumberElement" },
  { field: "cardExpiryElement", selector: "#cardExpiryElement" },
  { field: "cardCvcElement", selector: "#cardCvcElement" },
  { field: "billingZipCode", id: "billingZipCode" },
];

const DIETITIAN_SELECTION_PATH = "/register/account/dietitians";

const translateWithFallback = (t, key) => {
  const translated = t(key);
  if (translated !== key) {
    return translated;
  }
  return cardOnFileValidationMessageFallbacks[key] || translated;
};

const markCardOnFileCompleteLocally = (extra = {}) => {
  updateRegistrationData({
    isCardPresent: true,
    cardOnFile: {
      saved: true,
    },
    ...extra,
  });
};

const getCardOnFileStepMeta = () => {
  let isCardOnFileStepFour = false;
  const savedNav = localStorage.getItem("data");
  if (savedNav) {
    try {
      const parsed = JSON.parse(savedNav);
      isCardOnFileStepFour =
        parsed.paymentType === "insurance" ||
        parsed.paymentType === "selfpay" ||
        Boolean(parsed.coverageResult);
    } catch {
      isCardOnFileStepFour = false;
    }
  }

  return isCardOnFileStepFour
    ? CARD_ON_FILE_INSURANCE_STEP
    : CARD_ON_FILE_STEP;
};

const STRIPE_ELEMENT_STYLE = {
  base: {
    fontSize: "15px",
    lineHeight: "24px",
    color: "#2a2a2a",
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    "::placeholder": {
      color: REGISTRATION_PLACEHOLDER,
    },
  },
  invalid: {
    color: REGISTRATION_ERROR,
    iconColor: REGISTRATION_ERROR,
  },
};

const CARD_NUMBER_ELEMENT_OPTIONS = {
  showIcon: true,
  style: STRIPE_ELEMENT_STYLE,
};

const CARD_EXPIRY_ELEMENT_OPTIONS = {
  style: STRIPE_ELEMENT_STYLE,
};

const CARD_CVC_ELEMENT_OPTIONS = {
  style: STRIPE_ELEMENT_STYLE,
};

const STRIPE_BRAND_LABELS = {
  visa: "VISA",
  mastercard: "MASTERCARD",
  amex: "AMEX",
  discover: "DISCOVER",
};

const persistSetupIntentData = (setupIntentData) => {
  const clientSecret = extractSetupIntentClientSecret(setupIntentData);
  const setupIntentId = extractSetupIntentId(setupIntentData);
  updateRegistrationData({
    stripeSetupIntent: setupIntentData,
    ...(clientSecret ? { stripeSetupIntentClientSecret: clientSecret } : {}),
    ...(setupIntentId ? { stripeSetupIntentId: setupIntentId } : {}),
  });
  return { clientSecret, setupIntentId };
};

const formatPreviewCardNumber = (cardNumberState) => {
  if (!cardNumberState?.hasValue) {
    return "•••• •••• •••• ••••";
  }

  if (cardNumberState.last4) {
    return `•••• •••• •••• ${cardNumberState.last4}`;
  }

  return "•••• •••• •••• ••••";
};

const getLast4FromCardNumberEvent = (event) => event.last4 || "";

const formatExpiryFromStripeEvent = (event) => {
  const month =
    event.exp_month ?? event.expMonth ?? event.value?.expMonth ?? event.value?.exp_month;
  const year =
    event.exp_year ?? event.expYear ?? event.value?.expYear ?? event.value?.exp_year;

  if (month != null && year != null) {
    const mm = String(month).padStart(2, "0");
    const yy =
      String(year).length > 2
        ? String(year).slice(-2)
        : String(year).padStart(2, "0");
    return `${mm} / ${yy}`;
  }

  if (event.complete) {
    return null;
  }

  return "MM / YY";
};

const formatPreviewExpiry = (cardExpiryState, expiryPlaceholder) => {
  if (!cardExpiryState?.hasValue) {
    return expiryPlaceholder;
  }
  return cardExpiryState.display || expiryPlaceholder;
};

const formatExpiryFromPaymentMethodCard = (card) => {
  if (!card?.exp_month || !card?.exp_year) {
    return null;
  }
  const mm = String(card.exp_month).padStart(2, "0");
  const yy = String(card.exp_year).slice(-2);
  return `${mm} / ${yy}`;
};

const parseExpiryFromDisplay = (display) => {
  const match = String(display || "").match(/(\d{1,2})\s*\/\s*(\d{2,4})/);
  if (!match) {
    return { expMonth: null, expYear: null };
  }
  const expMonth = Number(match[1]);
  const yearPart = match[2];
  const expYear =
    yearPart.length === 2 ? Number(`20${yearPart}`) : Number(yearPart);
  return {
    expMonth: Number.isFinite(expMonth) ? expMonth : null,
    expYear: Number.isFinite(expYear) ? expYear : null,
  };
};

const applyTokenCardPreview = (card, setCardNumberState, setCardExpiryState, setCardBrand) => {
  if (!card) {
    return;
  }

  if (card.last4) {
    setCardNumberState((prev) => ({
      ...prev,
      hasValue: true,
      complete: true,
      last4: card.last4,
    }));
  }

  const expiryDisplay = formatExpiryFromPaymentMethodCard(card);
  if (expiryDisplay) {
    setCardExpiryState((prev) => ({
      ...prev,
      hasValue: true,
      complete: true,
      display: expiryDisplay,
    }));
  }

  if (card.brand) {
    setCardBrand(card.brand);
  }
};

const getCardPreview = (
  cardholderName,
  cardBrand,
  cardNumberState,
  cardExpiryState,
  t
) => ({
  previewNumber: formatPreviewCardNumber(cardNumberState),
  previewName:
    (cardholderName || "").trim().toUpperCase() ||
    t("NUTRITION_CARD_PREVIEW_LBL_DEFAULT_NAME"),
  previewExpiry: formatPreviewExpiry(
    cardExpiryState,
    t("NUTRITION_CARD_PREVIEW_PLACEHOLDER_EXPIRY")
  ),
  previewBrand:
    STRIPE_BRAND_LABELS[cardBrand] || t("NUTRITION_CARD_PREVIEW_LBL_BRAND"),
});

const renderCardSubtitleWithPolicyLink = (t, onOpenPolicy) => {
  const subtitle = t("NUTRITION_CARD_DESC_SUBTITLE");
  const linkLabel = t("NUTRITION_CANCELLATION_LBL_TITLE");
  const matchPhrases = [linkLabel, "cancellation policy"].filter(Boolean);

  for (const phrase of matchPhrases) {
    const index = subtitle.toLowerCase().lastIndexOf(phrase.toLowerCase());
    if (index === -1) continue;

    const before = subtitle.slice(0, index);
    const matched = subtitle.slice(index, index + phrase.length);
    const after = subtitle.slice(index + phrase.length);

    return (
      <>
        {before}
        <button
          type="button"
          className="policy-link"
          onClick={onOpenPolicy}
        >
          {matched}
        </button>
        {after}
      </>
    );
  }

  return (
    <>
      {subtitle}{" "}
      <button type="button" className="policy-link" onClick={onOpenPolicy}>
        {linkLabel}
      </button>
    </>
  );
};

const RegistrationSavedCardOnFile = ({
  savedCard,
  history,
  onChangeCard,
  isChangeCardLoading = false,
  canChangeCard = true,
}) => {
  const { t, i18n } = useTranslation();
  const stepTitleRef = useRegistrationStepFocus();
  const [isCancelPolicyOpen, setIsCancelPolicyOpen] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useLockViewportScroll();

  useEffect(() => {
    const registrationData = getRegistrationData();
    if (!registrationData.firstName && !registrationData.email) {
      history.replace("/register/account/payment");
      return;
    }
    if (!hasRegistrationSignupSession(registrationData)) {
      history.replace("/register/account/payment");
    }
  }, [history]);

  const fallbackCardholderName = (() => {
    if (savedCard?.cardholderName) {
      return savedCard.cardholderName;
    }
    const registrationData = getRegistrationData();
    return `${registrationData.firstName || ""} ${registrationData.lastName || ""}`.trim();
  })();
  const fallbackBillingZip =
    savedCard?.billingZipCode ||
    (() => {
      const registrationData = getRegistrationData();
      return (
        registrationData.billingZipCode ??
        registrationData.zipCode ??
        registrationData.zip ??
        ""
      );
    })();

  const cardNumberState = {
    hasValue: Boolean(savedCard?.last4),
    complete: Boolean(savedCard?.last4),
    last4: savedCard?.last4 || "",
  };
  const expiryDisplay =
    formatExpiryFromPaymentMethodCard({
      exp_month: savedCard?.expMonth,
      exp_year: savedCard?.expYear,
    }) || t("NUTRITION_CARD_PREVIEW_PLACEHOLDER_EXPIRY");
  const cardExpiryState = {
    hasValue: Boolean(
      formatExpiryFromPaymentMethodCard({
        exp_month: savedCard?.expMonth,
        exp_year: savedCard?.expYear,
      })
    ),
    complete: true,
    display: expiryDisplay,
  };
  const preview = getCardPreview(
    fallbackCardholderName,
    savedCard?.brand || "",
    cardNumberState,
    cardExpiryState,
    t
  );
  const cardStep = getCardOnFileStepMeta();
  const confirmDetails = {
    brand: savedCard?.brand || "",
    last4: savedCard?.last4 || "",
    expMonth: savedCard?.expMonth ?? null,
    expYear: savedCard?.expYear ?? null,
    cardholderName: fallbackCardholderName,
    billingZipCode: fallbackBillingZip,
  };

  const handleContinue = () => {
    if (isContinuing || isChangeCardLoading) {
      return;
    }
    setIsConfirmOpen(true);
  };

  const confirmContinue = () => {
    if (isContinuing || isChangeCardLoading) {
      return;
    }
    setIsContinuing(true);
    setIsConfirmOpen(false);
    markCardOnFileCompleteLocally();
    trackRegistrationStepCompleted("card_on_file", 4);
    history.replace(DIETITIAN_SELECTION_PATH);
  };

  const handleEdit = () => {
    if (!canChangeCard || isChangeCardLoading || isContinuing) {
      return;
    }
    setIsConfirmOpen(false);
    onChangeCard?.();
  };

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={cardStep}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={() => registrationStepBack(history)}
          />
        }
      >
        <div className="scroll-body">
          <div className="content-card">
            <form
              className="content-card-scroll"
              onSubmit={(e) => e.preventDefault()}
              noValidate
            >
              <div className="section-eyebrow">{t("NUTRITION_CARD_LBL_EYEBROW")}</div>
              <div className="card-on-file-header-row">
                <h2
                  className="section-title"
                  ref={stepTitleRef}
                  {...REGISTRATION_STEP_TITLE_PROPS}
                >
                  {t("NUTRITION_CARD_LBL_TITLE")}
                </h2>
                {canChangeCard ? (
                  <button
                    type="button"
                    className="btn-card-edit"
                    onClick={handleEdit}
                    disabled={isChangeCardLoading || isContinuing}
                    aria-label={translateWithFallback(
                      t,
                      "NUTRITION_CARD_BTN_EDIT"
                    )}
                    aria-busy={isChangeCardLoading}
                  >
                    {translateWithFallback(t, "NUTRITION_CARD_BTN_EDIT")}
                  </button>
                ) : null}
              </div>
              <p className="section-sub">
                {renderCardSubtitleWithPolicyLink(t, () =>
                  setIsCancelPolicyOpen(true)
                )}
              </p>

              <div className="cc-visual">
                <div className="cc-chip" />
                <div className="cc-number">{preview.previewNumber}</div>
                <div className="cc-meta">
                  <div>
                    <div className="cc-meta-label">
                      {t("NUTRITION_CARD_PREVIEW_LBL_CARDHOLDER")}
                    </div>
                    <div className="cc-meta-val">{preview.previewName}</div>
                  </div>
                  <div>
                    <div className="cc-meta-label">
                      {t("NUTRITION_CARD_PREVIEW_LBL_EXPIRES")}
                    </div>
                    <div className="cc-meta-val">{preview.previewExpiry}</div>
                  </div>
                </div>
                <div className="cc-brand">{preview.previewBrand}</div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="savedCardholderName">
                  {t("ADDCARD_PLACEHOLDER_CARDHOLDER_NAME")}{" "}
                  <RegistrationRequiredMark />
                </label>
                <input
                  id="savedCardholderName"
                  name="cardholderName"
                  type="text"
                  className="form-input"
                  value={fallbackCardholderName}
                  readOnly
                  disabled
                  autoComplete="cc-name"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="savedCardNumber">
                  {t("ADDCARD_PLACEHOLDER_CARD_NUMBER")}{" "}
                  <RegistrationRequiredMark />
                </label>
                <div className="stripe-card-element-wrap">
                  <div
                    id="savedCardNumber"
                    className="stripe-card-element-loading"
                  >
                    {preview.previewNumber}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="savedCardExpiry">
                    {t("NUTRITION_CARD_LBL_EXPIRY")}{" "}
                    <RegistrationRequiredMark />
                  </label>
                  <div className="stripe-card-element-wrap">
                    <div
                      id="savedCardExpiry"
                      className="stripe-card-element-loading"
                    >
                      {preview.previewExpiry}
                    </div>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="savedCardCvc">
                    {t("ADDCARD_PLACEHOLDER_CVV")}{" "}
                    <RegistrationRequiredMark />
                  </label>
                  <div className="stripe-card-element-wrap">
                    <div
                      id="savedCardCvc"
                      className="stripe-card-element-loading stripe-card-element-placeholder"
                    >
                      •••
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="savedBillingZipCode">
                  {t("NUTRITION_CARD_LBL_ZIP")}{" "}
                  <RegistrationRequiredMark />
                </label>
                <input
                  id="savedBillingZipCode"
                  name="billingZipCode"
                  type="text"
                  className="form-input"
                  value={fallbackBillingZip}
                  readOnly
                  disabled
                  inputMode="numeric"
                  maxLength={5}
                  autoComplete="postal-code"
                />
              </div>

              <div className="notice-box">
                <div className="notice-icon" aria-hidden>
                  ⚕️
                </div>
                <div className="notice-text">
                  {t("NUTRITION_CARD_DESC_NOTICE_BILLING_LEAD + …_REST")}
                </div>
              </div>

              <div className="notice-box nutrition">
                <div className="notice-icon" aria-hidden>
                  🛡️
                </div>
                <div className="notice-text">
                  {t("NUTRITION_CARD_DESC_NOTICE_STRIPE")}
                </div>
              </div>

              <button
                type="button"
                className="btn-primary"
                disabled={isContinuing || isChangeCardLoading}
                onClick={handleContinue}
              >
                {t("MORE_CHANGE_LANGUAGE_BTN_CONTINUE")}
                <RegistrationContinueArrow />
              </button>
            </form>
          </div>
        </div>
      </RegistrationLayout>
      <CancellationPolicyModal
        isOpen={isCancelPolicyOpen}
        onClose={() => setIsCancelPolicyOpen(false)}
        language={i18n.language}
      />
      <CardDetailsConfirmModal
        isOpen={isConfirmOpen}
        details={confirmDetails}
        confirmLabel={t("MORE_CHANGE_LANGUAGE_BTN_CONTINUE")}
        onConfirm={confirmContinue}
        onClose={() => setIsConfirmOpen(false)}
        isConfirming={isContinuing}
      />
    </RegistrationSetupWrapper>
  );
};

const RegistrationCardOnFileForm = (props) => {
  const {
    history,
    clientSecret,
    cardFormMode = "add",
    onChangeCardSuccess,
    onCancelChangeCard,
  } = props;
  const isChangingCard = cardFormMode === "change";
  const { t, i18n } = useTranslation();
  const stepTitleRef = useRegistrationStepFocus();
  const stripe = useStripe();
  const elements = useElements();
  const [isCancelPolicyOpen, setIsCancelPolicyOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formStatusMessage, setFormStatusMessage] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState(null);
  const [cardBrand, setCardBrand] = useState("");
  const [cardNumberState, setCardNumberState] = useState({
    hasValue: false,
    complete: false,
    last4: "",
  });
  const [cardExpiryState, setCardExpiryState] = useState({
    hasValue: false,
    complete: false,
    display: "MM / YY",
  });
  const [showCvcElement, setShowCvcElement] = useState(false);
  const isSubmittingRef = useRef(false);
  const cardPreviewSyncTimerRef = useRef(null);
  const {
    setFieldValue,
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    submitCount,
    handleSubmit,
    setFieldError,
    setFieldTouched,
    validateForm,
  } = props;

  useLockViewportScroll();

  useEffect(() => {
    const registrationData = getRegistrationData();
    if (!registrationData.firstName && !registrationData.email) {
      history.replace("/register/account/payment");
      return;
    }
    if (!hasRegistrationSignupSession(registrationData)) {
      history.replace("/register/account/payment");
      return;
    }
    const fullName = `${registrationData.firstName || ""} ${registrationData.lastName || ""}`.trim();
    setFieldValue("cardholderName", fullName);
    const billingZip =
      registrationData.billingZipCode ??
      registrationData.zipCode ??
      registrationData.zip ??
      "";
    if (billingZip) {
      setFieldValue("billingZipCode", billingZip);
    }
  }, [history, setFieldValue]);

  useEffect(
    () => () => {
      if (cardPreviewSyncTimerRef.current) {
        clearTimeout(cardPreviewSyncTimerRef.current);
      }
    },
    []
  );

  const scheduleCardPreviewSync = () => {
    if (!stripe || !elements) {
      return;
    }

    if (elements.getElement(CardCvcElement)) {
      return;
    }

    if (cardPreviewSyncTimerRef.current) {
      clearTimeout(cardPreviewSyncTimerRef.current);
    }

    cardPreviewSyncTimerRef.current = setTimeout(async () => {
      if (elements.getElement(CardCvcElement)) {
        return;
      }

      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        return;
      }

      const { token, error } = await stripe.createToken(cardNumberElement);
      if (error || !token?.card) {
        return;
      }

      applyTokenCardPreview(
        token.card,
        setCardNumberState,
        setCardExpiryState,
        setCardBrand
      );
    }, 150);
  };

  const mountCvcElement = () => {
    setShowCvcElement(true);
  };

  useEffect(() => {
    if (!showCvcElement || !elements) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      elements.getElement(CardCvcElement)?.focus?.();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [showCvcElement, elements]);

  const showError = (field) => {
    if ((errors[field] && touched[field]) || submitCount > 0) {
      const key = errors[field];
      const translated = t(key);
      const messageText =
        translated !== key
          ? translated
          : cardOnFileValidationMessageFallbacks[key] || translated;
      return (
        <RegistrationFieldError id={getRegistrationFieldErrorId(field)}>
          {messageText}
        </RegistrationFieldError>
      );
    }
    return null;
  };

  const fieldControlProps = (field, className) =>
    getRegistrationFieldControlProps({
      field,
      errors,
      touched,
      submitCount,
      className,
      required: true,
    });

  const isFieldInvalid = (field) =>
    Boolean(errors[field] && (touched[field] || submitCount > 0));

  const handleBillingZipChange = (e) => {
    setFieldValue(
      "billingZipCode",
      e.target.value.replace(/\D/g, "").slice(0, 5),
      true
    );
  };

  const handleCardNumberChange = (event) => {
    setCardBrand(event.brand || "");
    setCardNumberState({
      hasValue: !event.empty,
      complete: event.complete,
      last4: getLast4FromCardNumberEvent(event),
    });

    if (event.complete) {
      scheduleCardPreviewSync();
    }

    setFieldValue("cardNumberElement", event.complete ? "valid" : null, false);
    if (event.error) {
      setFieldError("cardNumberElement", event.error.message);
    } else {
      setFieldError("cardNumberElement", undefined);
    }
  };

  const handleCardExpiryChange = (event) => {
    if (event.empty) {
      setCardExpiryState({
        hasValue: false,
        complete: false,
        display: "MM / YY",
      });
    } else {
      const formattedExpiry = formatExpiryFromStripeEvent(event);
      setCardExpiryState((prev) => ({
        hasValue: true,
        complete: event.complete,
        display:
          formattedExpiry ||
          (event.complete ? prev.display : "•• / ••"),
      }));

      if (event.complete) {
        scheduleCardPreviewSync();
      }
    }

    setFieldValue("cardExpiryElement", event.complete ? "valid" : null, false);
    if (event.error) {
      setFieldError("cardExpiryElement", event.error.message);
    } else {
      setFieldError("cardExpiryElement", undefined);
    }
  };

  const handleCardCvcChange = (event) => {
    setFieldValue("cardCvcElement", event.complete ? "valid" : null, false);
    if (event.error) {
      setFieldError("cardCvcElement", event.error.message);
    } else {
      setFieldError("cardCvcElement", undefined);
    }
  };

  const buildCardConfirmDetails = (overrides = {}) => {
    const expiryFromDisplay = parseExpiryFromDisplay(cardExpiryState.display);
    return {
      brand: overrides.brand || cardBrand || "",
      last4: overrides.last4 || cardNumberState.last4 || "",
      expMonth: overrides.expMonth ?? expiryFromDisplay.expMonth,
      expYear: overrides.expYear ?? expiryFromDisplay.expYear,
      cardholderName:
        overrides.cardholderName || values.cardholderName.trim(),
      billingZipCode:
        overrides.billingZipCode || values.billingZipCode || "",
    };
  };

  const onSubmit = async () => {
    if (isSubmittingRef.current || isSaving || isConfirmOpen) {
      return;
    }

    const formErrors = await validateForm();
    if (Object.keys(formErrors).length > 0) {
      handleSubmit();
      window.requestAnimationFrame(() => {
        focusFirstInvalidRegistrationField(
          CARD_ON_FILE_FIELD_FOCUS_ORDER,
          formErrors
        );
      });
      return;
    }

    if (!stripe || !elements) {
      message.error(t("NUTRITION_CARD_ERR_FORM_LOADING"));
      return;
    }

    if (!showCvcElement) {
      mountCvcElement();
      message.warning(t("NUTRITION_CARD_ERR_ENTER_CVV"));
      return;
    }

    if (!clientSecret) {
      message.error(t("NUTRITION_COMMON_ERR_TRY_AGAIN"));
      return;
    }

    if (!elements.getElement(CardNumberElement)) {
      message.error(t("NUTRITION_CARD_ERR_FORM_LOADING"));
      return;
    }

    setConfirmDetails(buildCardConfirmDetails());
    setIsConfirmOpen(true);
  };

  const confirmSaveCard = async () => {
    if (isSubmittingRef.current || isSaving) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSaving(true);
    setFormStatusMessage("");

    let didSucceed = false;

    try {
      if (!stripe || !elements) {
        message.error(t("NUTRITION_CARD_ERR_FORM_LOADING"));
        return;
      }

      if (!clientSecret) {
        message.error(t("NUTRITION_COMMON_ERR_TRY_AGAIN"));
        return;
      }

      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        message.error(t("NUTRITION_CARD_ERR_FORM_LOADING"));
        return;
      }

      if (!isChangingCard) {
        const registrationData = getRegistrationData();
        const existingSetupIntentId = getStoredSetupIntentId(registrationData);
        if (
          existingSetupIntentId &&
          registrationData.cardStoredSetupIntentId === existingSetupIntentId
        ) {
          if (!isCardOnFileComplete(registrationData)) {
            markCardOnFileCompleteLocally();
          }
          didSucceed = true;
          trackRegistrationStepCompleted("card_on_file", 4);
          setIsConfirmOpen(false);
          history.replace(DIETITIAN_SELECTION_PATH);
          return;
        }
      }

      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: values.cardholderName.trim(),
            address: {
              postal_code: values.billingZipCode,
            },
          },
        },
      });

      if (result.error) {
        setFieldError("cardNumberElement", result.error.message);
        message.error(result.error.message);
        return;
      }

      if (result.setupIntent?.status !== "succeeded") {
        message.error(t("NUTRITION_CARD_ERR_INVALID_CARD"));
        return;
      }

      if (isChangingCard) {
        const paymentMethodId = extractPaymentMethodIdFromSetupIntent(
          result.setupIntent
        );
        if (!paymentMethodId) {
          message.error(t("NUTRITION_COMMON_ERR_TRY_AGAIN"));
          return;
        }

        const changeRes = await AuthenticationApiSerivce.changeRegistrationCard(
          { new_payment_method_id: paymentMethodId },
          i18n.language,
          { serviceId: getRegistrationServiceId(getRegistrationData()) }
        );

        if (!isStripeApiSuccess(changeRes)) {
          const apiMessage = changeRes?.message || "";
          const lowerMessage = apiMessage.toLowerCase();
          if (lowerMessage.includes("only be changed during registration")) {
            message.error(
              apiMessage ||
                translateWithFallback(
                  t,
                  "NUTRITION_CARD_ERR_CHANGE_NOT_ALLOWED"
                )
            );
          } else {
            message.error(
              apiMessage ||
                translateWithFallback(t, "NUTRITION_CARD_ERR_CHANGE_FAILED")
            );
          }
          return;
        }

        let nextSavedCard = normalizeRegistrationCardFromApi(
          changeRes?.data ?? changeRes
        );
        if (!nextSavedCard.last4 && !nextSavedCard.brand) {
          try {
            const cardListRes =
              await AuthenticationApiSerivce.getRegistrationCardList(
                i18n.language,
                { serviceId: getRegistrationServiceId(getRegistrationData()) }
              );
            if (isStripeApiSuccess(cardListRes)) {
              nextSavedCard =
                pickDefaultRegistrationCard(
                  extractRegistrationCardsFromListResponse(cardListRes)
                ) || nextSavedCard;
            }
          } catch {
            // Keep response-normalized card if refresh fails.
          }
        }

        markCardOnFileCompleteLocally({
          cardStoredSetupIntentId: result.setupIntent.id,
        });
        didSucceed = true;
        setIsConfirmOpen(false);
        onChangeCardSuccess?.(
          buildCardConfirmDetails({
            brand: nextSavedCard.brand || cardBrand || "",
            last4: nextSavedCard.last4 || cardNumberState.last4 || "",
            expMonth: nextSavedCard.expMonth ?? null,
            expYear: nextSavedCard.expYear ?? null,
            cardholderName:
              nextSavedCard.cardholderName || values.cardholderName.trim(),
            billingZipCode:
              nextSavedCard.billingZipCode || values.billingZipCode || "",
          })
        );
        return;
      }

      const setupIntentId = result.setupIntent.id;
      if (!setupIntentId) {
        message.error(t("NUTRITION_COMMON_ERR_TRY_AGAIN"));
        return;
      }

      const storeCardRes = await AuthenticationApiSerivce.storeRegistrationCard(
        {
          setup_intent_id: setupIntentId,
          is_default_payment: true,
        },
        i18n.language
      );

      if (!isStripeApiSuccess(storeCardRes)) {
        message.error(storeCardRes?.message || t("NUTRITION_CARD_ERR_SAVE_CARD"));
        return;
      }

      const lastFour = extractCardLastFour(result.setupIntent, storeCardRes);
      if (lastFour) {
        setCardNumberState((prev) => ({
          ...prev,
          complete: true,
          last4: lastFour,
        }));
      }

      const latestRegistrationData = getRegistrationData();
      markCardOnFileCompleteLocally({
        cardStoredSetupIntentId: setupIntentId,
        ...(latestRegistrationData.paymentType !== "insurance"
          ? { paymentType: "selfpay" }
          : {}),
      });
      didSucceed = true;
      trackRegistrationStepCompleted("card_on_file", 4);
      setIsConfirmOpen(false);
      history.replace(DIETITIAN_SELECTION_PATH);
    } catch (err) {
      trackRegistrationError({
        stepName: "card_on_file",
        stepNumber: 4,
        errorType: "api_failed",
      });
      message.error(
        resolveUserSubmitErrorMessage(err, {
          fallback: isChangingCard
            ? translateWithFallback(t, "NUTRITION_CARD_ERR_CHANGE_FAILED")
            : t("NUTRITION_COMMON_ERR_TRY_AGAIN"),
          translate: t,
        })
      );
    } finally {
      if (!didSucceed) {
        isSubmittingRef.current = false;
        setIsSaving(false);
        setFormStatusMessage("");
        setIsConfirmOpen(false);
      }
    }
  };

  const preview = getCardPreview(
    values.cardholderName,
    cardBrand,
    cardNumberState,
    cardExpiryState,
    t
  );

  const stripeReady = Boolean(stripe && elements);

  const cardStep = getCardOnFileStepMeta();
  const handleFormBack = () => {
    if (isChangingCard) {
      onCancelChangeCard?.();
      return;
    }
    registrationStepBack(history);
  };

  const handleCloseCardConfirm = () => {
    if (isSaving) {
      return;
    }
    setIsConfirmOpen(false);
    setConfirmDetails(null);
  };

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={cardStep}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={handleFormBack}
          />
        }
      >
        <div className="scroll-body">
          <div className="content-card">
            <form
              className="content-card-scroll"
              onSubmit={(e) => e.preventDefault()}
              noValidate
            >
              <div className="section-eyebrow">{t("NUTRITION_CARD_LBL_EYEBROW")}</div>
              <h2
                className="section-title"
                ref={stepTitleRef}
                {...REGISTRATION_STEP_TITLE_PROPS}
              >
                {t("NUTRITION_CARD_LBL_TITLE")}
              </h2>
              <p className="section-sub">
                {renderCardSubtitleWithPolicyLink(t, () =>
                  setIsCancelPolicyOpen(true)
                )}
              </p>

              {formStatusMessage ? (
                <RegistrationAnnouncedText
                  className="payment-method-review-status"
                  level="important"
                  message={formStatusMessage}
                />
              ) : null}

              <div className="cc-visual">
                <div className="cc-chip" />
                <div className="cc-number">{preview.previewNumber}</div>
                <div className="cc-meta">
                  <div>
                    <div className="cc-meta-label">
                      {t("NUTRITION_CARD_PREVIEW_LBL_CARDHOLDER")}
                    </div>
                    <div className="cc-meta-val">{preview.previewName}</div>
                  </div>
                  <div>
                    <div className="cc-meta-label">
                      {t("NUTRITION_CARD_PREVIEW_LBL_EXPIRES")}
                    </div>
                    <div className="cc-meta-val">{preview.previewExpiry}</div>
                  </div>
                </div>
                <div className="cc-brand">{preview.previewBrand}</div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="cardholderName">
                  {t("ADDCARD_PLACEHOLDER_CARDHOLDER_NAME")}{" "}
                  <RegistrationRequiredMark />
                </label>
                <input
                  id="cardholderName"
                  name="cardholderName"
                  type="text"
                  placeholder={t("NUTRITION_CARD_PLACEHOLDER_CARDHOLDER")}
                  value={values.cardholderName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="cc-name"
                  {...fieldControlProps("cardholderName", "form-input")}
                />
                {showError("cardholderName")}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="cardNumberElement">
                  {t("ADDCARD_PLACEHOLDER_CARD_NUMBER")}{" "}
                  <RegistrationRequiredMark />
                </label>
                <div
                  className={clsx(
                    "stripe-card-element-wrap",
                    isFieldInvalid("cardNumberElement") && "form-input--invalid"
                  )}
                >
                  <CardNumberElement
                    id="cardNumberElement"
                    options={{
                      ...CARD_NUMBER_ELEMENT_OPTIONS,
                      ariaLabel: t("ADDCARD_PLACEHOLDER_CARD_NUMBER"),
                    }}
                    onChange={handleCardNumberChange}
                    onBlur={() => {
                      setFieldTouched("cardNumberElement", true);
                      scheduleCardPreviewSync();
                    }}
                  />
                </div>
                {showError("cardNumberElement")}
              </div>

              <div className="form-row">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="cardExpiryElement">
                    {t("NUTRITION_CARD_LBL_EXPIRY")}{" "}
                    <RegistrationRequiredMark />
                  </label>
                  <div
                    className={clsx(
                      "stripe-card-element-wrap",
                      isFieldInvalid("cardExpiryElement") && "form-input--invalid"
                    )}
                  >
                    <CardExpiryElement
                      id="cardExpiryElement"
                      options={{
                        ...CARD_EXPIRY_ELEMENT_OPTIONS,
                        ariaLabel: t("NUTRITION_CARD_LBL_EXPIRY"),
                      }}
                      onChange={handleCardExpiryChange}
                      onBlur={() => {
                        setFieldTouched("cardExpiryElement", true);
                        scheduleCardPreviewSync();
                      }}
                    />
                  </div>
                  {showError("cardExpiryElement")}
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="cardCvcElement">
                    {t("ADDCARD_PLACEHOLDER_CVV")}{" "}
                    <RegistrationRequiredMark />
                  </label>
                  <div
                    className={clsx(
                      "stripe-card-element-wrap",
                      isFieldInvalid("cardCvcElement") && "form-input--invalid"
                    )}
                    onClick={mountCvcElement}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        mountCvcElement();
                      }
                    }}
                  >
                    {stripeReady && showCvcElement ? (
                      <CardCvcElement
                        id="cardCvcElement"
                        options={{
                          ...CARD_CVC_ELEMENT_OPTIONS,
                          ariaLabel: t("ADDCARD_PLACEHOLDER_CVV"),
                        }}
                        onChange={handleCardCvcChange}
                        onBlur={() => setFieldTouched("cardCvcElement", true)}
                      />
                    ) : (
                      <div
                        id="cardCvcElement"
                        className="stripe-card-element-loading stripe-card-element-placeholder"
                        role="button"
                        tabIndex={0}
                        aria-label={t("NUTRITION_CARD_ARIA_ENTER_CVV")}
                      >
                        •••
                      </div>
                    )}
                  </div>
                  {showError("cardCvcElement")}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="billingZipCode">
                  {t("NUTRITION_CARD_LBL_ZIP")}{" "}
                  <RegistrationRequiredMark />
                </label>
                <input
                  id="billingZipCode"
                  name="billingZipCode"
                  type="text"
                  placeholder={t("NUTRITION_CARD_PLACEHOLDER_ZIP")}
                  value={values.billingZipCode}
                  onChange={handleBillingZipChange}
                  onBlur={handleBlur}
                  inputMode="numeric"
                  maxLength={5}
                  autoComplete="postal-code"
                  {...fieldControlProps("billingZipCode", "form-input")}
                />
                {showError("billingZipCode")}
              </div>

              <div className="notice-box">
                <div className="notice-icon" aria-hidden>
                  ⚕️
                </div>
                <div className="notice-text">
                  {t("NUTRITION_CARD_DESC_NOTICE_BILLING_LEAD + …_REST")}
                </div>
              </div>

              <div className="notice-box nutrition">
                <div className="notice-icon" aria-hidden>
                  🛡️
                </div>
                <div className="notice-text">
                  {t("NUTRITION_CARD_DESC_NOTICE_STRIPE")}
                </div>
              </div>

              <button
                type="button"
                className="btn-primary"
                disabled={isSaving || isConfirmOpen || !stripeReady || !clientSecret}
                onClick={onSubmit}
                aria-busy={isSaving}
              >
                {isChangingCard ? (
                  <>
                    {t("MORE_CHANGE_LANGUAGE_BTN_CONTINUE")}
                    <RegistrationContinueArrow />
                  </>
                ) : (
                  t("NUTRITION_CARD_BTN_SAVE")
                )}
              </button>
              {isChangingCard ? (
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={isSaving}
                  onClick={() => onCancelChangeCard?.()}
                >
                  {translateWithFallback(t, "NUTRITION_CARD_BTN_CANCEL_CHANGE")}
                </button>
              ) : null}
            </form>
          </div>
        </div>
      </RegistrationLayout>
      <CancellationPolicyModal
        isOpen={isCancelPolicyOpen}
        onClose={() => setIsCancelPolicyOpen(false)}
        language={i18n.language}
      />
      <CardDetailsConfirmModal
        isOpen={isConfirmOpen}
        details={confirmDetails}
        onConfirm={confirmSaveCard}
        onClose={handleCloseCardConfirm}
        isConfirming={isSaving}
      />
    </RegistrationSetupWrapper>
  );
};

const RegistrationCardOnFileFormEnhanced = compose(
  withRouter,
  enhancer
)(RegistrationCardOnFileForm);

const ensureStripeSetupIntent = async ({
  registrationData,
  language,
  signal,
  history,
  stripePromiseRef,
  setStripePromise,
}) => {
  let publishableKey =
    registrationData.stripePublishableKey ||
    extractPublishableKey(registrationData.savePaymentTypeResponse);

  if (!publishableKey) {
    const tokenRes =
      await AuthenticationApiSerivce.getRegistrationStripePublishableToken(
        language,
        { signal }
      );
    if (redirectIfProviderUnavailableFromResponse(history, tokenRes)) {
      return null;
    }
    if (!isStripeApiSuccess(tokenRes)) {
      return null;
    }
    publishableKey = extractPublishableKey(tokenRes);
  }

  if (!publishableKey || typeof publishableKey !== "string") {
    return null;
  }

  const trimmedKey = publishableKey.trim();
  if (!stripePromiseRef.current) {
    stripePromiseRef.current = loadStripe(trimmedKey);
  }
  setStripePromise(stripePromiseRef.current);

  const setupIntentRes =
    await AuthenticationApiSerivce.createRegistrationSetupIntent(
      { stripe_publishable_key: trimmedKey },
      language,
      { signal }
    );
  if (!isStripeApiSuccess(setupIntentRes)) {
    return null;
  }

  const setupIntentData = setupIntentRes?.data ?? setupIntentRes;
  updateRegistrationData({ stripePublishableKey: trimmedKey });
  const persisted = persistSetupIntentData(setupIntentData);
  if (!persisted.clientSecret) {
    return null;
  }

  return persisted.clientSecret;
};

const RegistrationCardOnFile = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [savedCard, setSavedCard] = useState(null);
  const [isChangingCard, setIsChangingCard] = useState(false);
  const [isChangeCardLoading, setIsChangeCardLoading] = useState(false);
  const [isStripeConfigLoading, setIsStripeConfigLoading] = useState(true);
  const stripePromiseRef = useRef(null);

  useEffect(() => {
    const registrationData = getRegistrationData();
    if (!hasRegistrationSignupSession(registrationData)) {
      if (registrationData.paymentType === "selfpay") {
        history.replace("/register/account/payment");
      } else if (registrationData.coverageResult) {
        history.replace("/register/account/insurance/coverage");
      } else {
        history.replace("/register/account/payment");
      }
    }
  }, [history]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const loadCardOnFilePage = async () => {
      const registrationData = getRegistrationData();
      if (!hasRegistrationSignupSession(registrationData)) {
        setIsStripeConfigLoading(false);
        return;
      }

      if (redirectIfProviderUnavailableBeforeApi(history, registrationData)) {
        setIsStripeConfigLoading(false);
        return;
      }

      setIsStripeConfigLoading(true);
      setSavedCard(null);
      setIsChangingCard(false);
      setClientSecret(null);

      try {
        const { isCardPresent } = getStoredPaymentTypeFields(registrationData);
        const serviceId = getRegistrationServiceId(registrationData);

        if (isCardPresent) {
          try {
            const cardListRes =
              await AuthenticationApiSerivce.getRegistrationCardList(
                i18n.language,
                { signal: controller.signal, serviceId }
              );
            if (cancelled) {
              return;
            }

            if (isStripeApiSuccess(cardListRes)) {
              const cards =
                extractRegistrationCardsFromListResponse(cardListRes);
              const existingSavedCard = pickDefaultRegistrationCard(cards);
              if (existingSavedCard) {
                markCardOnFileCompleteLocally();
                setSavedCard(existingSavedCard);
                setIsStripeConfigLoading(false);
                return;
              }
            }
          } catch (cardListErr) {
            if (
              cancelled ||
              isRequestCancelledError(cardListErr) ||
              isRegistrationLoadRetryExhaustedError(cardListErr)
            ) {
              return;
            }
          }
        }

        const nextClientSecret = await ensureStripeSetupIntent({
          registrationData: getRegistrationData(),
          language: i18n.language,
          signal: controller.signal,
          history,
          stripePromiseRef,
          setStripePromise,
        });
        if (cancelled) {
          return;
        }
        if (!nextClientSecret) {
          return;
        }
        setIsStripeConfigLoading(false);
        setClientSecret(nextClientSecret);
      } catch (err) {
        if (
          cancelled ||
          isRequestCancelledError(err) ||
          isRegistrationLoadRetryExhaustedError(err)
        ) {
          return;
        }
      } finally {
        if (!cancelled) {
          setIsStripeConfigLoading(false);
        }
      }
    };

    loadCardOnFilePage();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [history, i18n.language]);

  const handleStartChangeCard = async () => {
    if (isChangeCardLoading || isRegistrationComplete()) {
      if (isRegistrationComplete()) {
        message.error(
          translateWithFallback(t, "NUTRITION_CARD_ERR_CHANGE_NOT_ALLOWED")
        );
      }
      return;
    }

    setIsChangeCardLoading(true);
    const controller = new AbortController();
    try {
      const nextClientSecret = await ensureStripeSetupIntent({
        registrationData: getRegistrationData(),
        language: i18n.language,
        signal: controller.signal,
        history,
        stripePromiseRef,
        setStripePromise,
      });
      if (!nextClientSecret) {
        message.error(t("NUTRITION_COMMON_ERR_TRY_AGAIN"));
        return;
      }
      setClientSecret(nextClientSecret);
      setIsChangingCard(true);
    } catch (err) {
      if (!isRequestCancelledError(err)) {
        message.error(
          resolveUserSubmitErrorMessage(err, {
            fallback: translateWithFallback(t, "NUTRITION_CARD_ERR_CHANGE_FAILED"),
            translate: t,
          })
        );
      }
    } finally {
      setIsChangeCardLoading(false);
    }
  };

  const handleCancelChangeCard = () => {
    setIsChangingCard(false);
    setClientSecret(null);
  };

  const handleChangeCardSuccess = () => {
    markCardOnFileCompleteLocally();
    trackRegistrationStepCompleted("card_on_file", 4);
    history.replace(DIETITIAN_SELECTION_PATH);
  };

  if (
    isStripeConfigLoading ||
    (!savedCard && !stripePromise) ||
    (isChangingCard && (!stripePromise || !clientSecret))
  ) {
    return (
      <RegistrationSetupWrapper>
        <RegistrationLayout containScroll>
          <div className="scroll-body">
            <div className="content-card">
              <div className="content-card-scroll">
                <RegistrationAnnouncedText
                  message={t("NUTRITION_CARD_LBL_LOADING_PAYMENT_FORM")}
                />
              </div>
            </div>
          </div>
        </RegistrationLayout>
      </RegistrationSetupWrapper>
    );
  }

  if (savedCard && !isChangingCard) {
    return (
      <RegistrationSavedCardOnFile
        savedCard={savedCard}
        history={history}
        onChangeCard={handleStartChangeCard}
        isChangeCardLoading={isChangeCardLoading}
        canChangeCard={!isRegistrationComplete()}
      />
    );
  }

  if (!stripePromise || !clientSecret) {
    return (
      <RegistrationSetupWrapper>
        <RegistrationLayout containScroll>
          <div className="scroll-body">
            <div className="content-card">
              <div className="content-card-scroll">
                <RegistrationAnnouncedText
                  message={t("NUTRITION_CARD_LBL_LOADING_PAYMENT_FORM")}
                />
              </div>
            </div>
          </div>
        </RegistrationLayout>
      </RegistrationSetupWrapper>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <RegistrationCardOnFileFormEnhanced
        clientSecret={clientSecret}
        cardFormMode={isChangingCard ? "change" : "add"}
        onChangeCardSuccess={handleChangeCardSuccess}
        onCancelChangeCard={handleCancelChangeCard}
      />
    </Elements>
  );
};

export default RegistrationCardOnFile;
